/**
 * PrivacyKpiDashboard.tsx
 * 
 * PRIV-25.2: Privacy & Security KPI Dashboard for VeraScore Admins and Auditors
 * 
 * This dashboard displays privacy metrics from the privacy_kpi_summary view.
 * Access is restricted via ProtectedRoute to verascore_admin and auditor roles.
 */

import { FC, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import {
  FiShield,
  FiAlertTriangle,
  FiActivity,
  FiKey,
  FiFileText,
  FiTrash2,
  FiRefreshCw,
  FiCheckCircle,
  FiXCircle,
  FiClock,
} from 'react-icons/fi';
import AdminDeletionPanel from '../components/privacy/AdminDeletionPanel';
import AdminMemberVisibilityPanel from '../components/privacy/AdminMemberVisibilityPanel';
import AdminCreditAuditPanel from '../components/privacy/AdminCreditAuditPanel';
import useAppState from '../common/AppState';
import useHasRole from '../hooks/useHasRole';

// Types for KPI data
interface PrivacyKpiSummary {
  critical_findings_open: number;
  findings_last_7_days: number;
  pii_access_events_30d: number;
  break_glass_events_30d: number;
  dsar_pending: number;
  dsar_on_time: number;
  dsar_overdue: number;
  deletion_jobs_completed_30d: number;
  deletion_jobs_failed_30d: number;
  computed_at: string;
}

interface KpiTileProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  status: 'good' | 'warning' | 'critical' | 'neutral';
  subtitle?: string;
}

// KPI Tile Component
const KpiTile: FC<KpiTileProps> = ({ title, value, icon, status, subtitle }) => {
  const statusColors = {
    good: 'bg-green-50 border-green-200 text-green-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    critical: 'bg-red-50 border-red-200 text-red-800',
    neutral: 'bg-slate-50 border-slate-200 text-slate-800',
  };

  const iconColors = {
    good: 'text-green-600',
    warning: 'text-yellow-600',
    critical: 'text-red-600',
    neutral: 'text-slate-600',
  };

  return (
    <div className={`p-4 rounded-lg border-2 ${statusColors[status]} transition-all hover:shadow-md`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium opacity-75">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {subtitle && <p className="text-xs opacity-60 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2 rounded-lg bg-white/50 ${iconColors[status]}`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

// Tab type
type TabType = 'overview' | 'findings' | 'break-glass' | 'access' | 'deletion' | 'token-security' | 'visibility' | 'credit-audit';

// ADMIN-HIDE-001: Only these Auth0 IDs can see the Member Visibility tab
const VISIBILITY_ADMIN_IDS: string[] = [
  'auth0|68c0b202d0138ee30e93adc7', // jim - dev
  'auth0|698d61ef869d2aad03d5fa44', // jim - prod
];

const PrivacyKpiDashboard: FC = () => {
  const [kpiData, setKpiData] = useState<PrivacyKpiSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const { profile } = useAppState();

  // ADMIN-HIDE-001: Check if current user can manage member visibility
  const canManageVisibility = Boolean(
    profile && (profile.sub || profile.user_id) &&
    VISIBILITY_ADMIN_IDS.includes((profile.sub || profile.user_id) as string)
  );

  // CC-211-AUDIT Phase 2I: Credit Completeness admin audit tab (Platform Admin only)
  const canAccessCreditAudit = useHasRole(['Platform Admin']);

  const fetchKpiData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: queryError } = await supabase
        .from('privacy_kpi_summary')
        .select('*')
        .single();

      if (queryError) {
        // Check if the view doesn't exist
        if (queryError.message?.includes('relation "privacy_kpi_summary" does not exist') ||
            queryError.code === '42P01') {
          throw new Error('Privacy KPI database view not yet created. Please run migration: PRIV-25-1-CREATE-PRIVACY-KPI-VIEWS.sql');
        }
        throw queryError;
      }
      setKpiData(data);
      setLastRefresh(new Date());
    } catch (err) {
      if (import.meta.env.DEV) console.error('[PrivacyKpiDashboard] Error fetching KPIs:', err);
      setError(err instanceof Error ? err.message : 'Failed to load KPI data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKpiData();
  }, [fetchKpiData]);

  // Helper to determine KPI status
  const getStatus = (value: number, thresholds: { good: number; warning: number }): 'good' | 'warning' | 'critical' => {
    if (value <= thresholds.good) return 'good';
    if (value <= thresholds.warning) return 'warning';
    return 'critical';
  };

  const tabs: { id: TabType; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'findings', label: 'PII Findings' },
    { id: 'break-glass', label: 'Break-Glass Events' },
    { id: 'access', label: 'Access by Purpose' },
    { id: 'deletion', label: 'Member Deletion' },
    { id: 'token-security', label: 'Token Security' },
    // ADMIN-HIDE-001: Only show to authorized admins
    ...(canManageVisibility ? [{ id: 'visibility' as TabType, label: '🔒 Member Visibility' }] : []),
    // CC-211-AUDIT Phase 2I: Platform Admin only
    ...(canAccessCreditAudit ? [{ id: 'credit-audit' as TabType, label: 'Credit Audit' }] : []),
  ];

  // CC-211-AUDIT Phase 2I: Auto-activate credit-audit tab when deep-linked
  //   - via ?tab=credit-audit (header dropdown click)
  //   - via ?memberId=... (shareable lineage link)
  useEffect(() => {
    if (!canAccessCreditAudit) return;
    const params = new URLSearchParams(window.location.search);
    const shouldActivate = params.get('tab') === 'credit-audit' || !!params.get('memberId');
    if (shouldActivate && activeTab !== 'credit-audit') {
      setActiveTab('credit-audit');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessCreditAudit]);

  if (loading && !kpiData) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading Privacy KPIs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const isMigrationError = error.includes('PRIV-25-1-CREATE-PRIVACY-KPI-VIEWS.sql');

    return (
      <div className="p-8 max-w-4xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <FiAlertTriangle className="w-8 h-8 text-yellow-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                {isMigrationError ? 'Database Setup Required' : 'Error Loading KPIs'}
              </h3>
              <p className="text-yellow-700 mb-4">{error}</p>

              {isMigrationError && (
                <div className="bg-white border border-yellow-300 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-slate-800 mb-2">📋 Setup Instructions:</h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-slate-700">
                    <li>Navigate to: <code className="bg-slate-100 px-2 py-1 rounded text-xs">api-verascore-platform/sql-scripts/Migrations/</code></li>
                    <li>Run migration: <code className="bg-slate-100 px-2 py-1 rounded text-xs">PRIV-25-1-CREATE-PRIVACY-KPI-VIEWS.sql</code></li>
                    <li>This creates the following database objects:
                      <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                        <li><code className="text-xs">privacy_events</code> table</li>
                        <li><code className="text-xs">pii_findings</code> table</li>
                        <li><code className="text-xs">dsar_requests</code> table</li>
                        <li><code className="text-xs">privacy_jobs</code> table</li>
                        <li><code className="text-xs">privacy_kpi_summary</code> view (main dashboard)</li>
                        <li>7 additional KPI views</li>
                      </ul>
                    </li>
                    <li>Refresh this page after migration completes</li>
                  </ol>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={fetchKpiData}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center gap-2"
                >
                  <FiRefreshCw size={16} />
                  Retry
                </button>
                <button
                  onClick={() => window.history.back()}
                  className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300"
                >
                  Go Back
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FiShield className="text-blue-600" />
            Privacy & Security KPIs
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Compliance monitoring dashboard • PRIV-25
          </p>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs text-slate-400">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </span>
          <button
            onClick={fetchKpiData}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <FiRefreshCw className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 mb-6 border-b border-slate-200">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'text-blue-600 border-blue-600'
                : 'text-slate-500 border-transparent hover:text-slate-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* KPI Tiles Grid - Overview Tab */}
      {activeTab === 'overview' && kpiData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <KpiTile
            title="Critical Findings Open"
            value={kpiData.critical_findings_open}
            icon={<FiAlertTriangle size={20} />}
            status={getStatus(kpiData.critical_findings_open, { good: 0, warning: 2 })}
            subtitle="Critical/High severity PII findings"
          />
          <KpiTile
            title="Findings (7 days)"
            value={kpiData.findings_last_7_days}
            icon={<FiFileText size={20} />}
            status={getStatus(kpiData.findings_last_7_days, { good: 5, warning: 15 })}
            subtitle="New PII findings this week"
          />
          <KpiTile
            title="PII Access Events (30d)"
            value={kpiData.pii_access_events_30d}
            icon={<FiActivity size={20} />}
            status="neutral"
            subtitle="Purpose-logged access events"
          />
          <KpiTile
            title="Break-Glass Events (30d)"
            value={kpiData.break_glass_events_30d}
            icon={<FiKey size={20} />}
            status={getStatus(kpiData.break_glass_events_30d, { good: 0, warning: 3 })}
            subtitle="Emergency access events"
          />
          <KpiTile
            title="DSAR Pending"
            value={kpiData.dsar_pending}
            icon={<FiClock size={20} />}
            status={getStatus(kpiData.dsar_pending, { good: 5, warning: 15 })}
            subtitle="Open data subject requests"
          />
          <KpiTile
            title="Deletion Jobs (30d)"
            value={`${kpiData.deletion_jobs_completed_30d} / ${kpiData.deletion_jobs_completed_30d + kpiData.deletion_jobs_failed_30d}`}
            icon={<FiTrash2 size={20} />}
            status={kpiData.deletion_jobs_failed_30d > 0 ? 'warning' : 'good'}
            subtitle={kpiData.deletion_jobs_failed_30d > 0 ? `${kpiData.deletion_jobs_failed_30d} failed` : 'All successful'}
          />
        </div>
      )}

      {/* DSAR SLA Summary */}
      {activeTab === 'overview' && kpiData && (
        <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <h3 className="font-semibold text-slate-800 mb-3">DSAR SLA Compliance</h3>
          <div className="flex gap-8">
            <div className="flex items-center gap-2">
              <FiCheckCircle className="text-green-600" />
              <span className="text-sm">{kpiData.dsar_on_time} On Time</span>
            </div>
            <div className="flex items-center gap-2">
              <FiXCircle className="text-red-600" />
              <span className="text-sm">{kpiData.dsar_overdue} Overdue</span>
            </div>
            <div className="flex items-center gap-2">
              <FiClock className="text-yellow-600" />
              <span className="text-sm">{kpiData.dsar_pending} Pending</span>
            </div>
          </div>
        </div>
      )}

      {/* Deletion Tab - PRIV-200.8 */}
      {activeTab === 'deletion' && (
        <AdminDeletionPanel />
      )}

      {/* Token Security Tab - PLAID-PORT-TO-PROD-001 */}
      {activeTab === 'token-security' && (
        <div className="bg-slate-50 rounded-lg border border-slate-200 p-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 mb-4">
              <FiKey size={24} className="text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Plaid Token Security</h3>
            <p className="text-slate-500 mb-4">
              Manage Plaid access token rotation for connected member accounts.
            </p>
            <div className="bg-white rounded-lg border border-slate-200 p-4 max-w-lg mx-auto text-left">
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Planned Features:</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• View all active Plaid items with institution and member details</li>
                <li>• Rotate compromised tokens via <code className="text-xs bg-slate-100 px-1 rounded">POST /verascore/token-rotate</code></li>
                <li>• View rotation history from <code className="text-xs bg-slate-100 px-1 rounded">privacy_events</code> audit log</li>
                <li>• Confirmation modal with security warnings before rotation</li>
              </ul>
            </div>
            <p className="text-xs text-slate-400 mt-4">
              Backend endpoint ready • API: POST /verascore/token-rotate (admin-only)
            </p>
          </div>
        </div>
      )}

      {/* ADMIN-HIDE-001: Member Visibility Tab */}
      {activeTab === 'visibility' && canManageVisibility && (
        <AdminMemberVisibilityPanel />
      )}

      {/* CC-211-AUDIT Phase 2I: Credit Completeness admin audit tab */}
      {activeTab === 'credit-audit' && canAccessCreditAudit && (
        <AdminCreditAuditPanel />
      )}

      {/* Placeholder for other tabs */}
      {activeTab !== 'overview' && activeTab !== 'deletion' && activeTab !== 'token-security' && activeTab !== 'visibility' && activeTab !== 'credit-audit' && (
        <div className="bg-slate-50 rounded-lg border border-slate-200 p-8 text-center">
          <p className="text-slate-500">
            {activeTab === 'findings' && 'PII Findings drill-down - Coming soon'}
            {activeTab === 'break-glass' && 'Break-Glass Events audit log - Coming soon'}
            {activeTab === 'access' && 'Access by Purpose breakdown - Coming soon'}
          </p>
          <p className="text-xs text-slate-400 mt-2">
            Data available in: {activeTab === 'findings' ? 'pii_findings_weekly' :
              activeTab === 'break-glass' ? 'break_glass_events' : 'pii_access_by_purpose'} view
          </p>
        </div>
      )}

      {/* Footer */}
      <div className="mt-8 text-center text-xs text-slate-400">
        <p>Data source: privacy_kpi_summary view • RLS restricted to admin/ops roles</p>
        {kpiData?.computed_at && (
          <p>Computed at: {new Date(kpiData.computed_at).toLocaleString()}</p>
        )}
      </div>
    </div>
  );
};

export default PrivacyKpiDashboard;

